// src/components/forms/ProfileForm.jsx
import { useState, useEffect } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase-client';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import TextInput from './formComponents/TextInput';
import DateInput from './formComponents/DateInput';
import UsernameInput from './formComponents/UsernameInput';
import SubmitButton from './formComponents/SubmitButton';
import ErrorMessage from './formComponents/ErrorMessage';
import { offlineWriteMessage, useOnlineStatus } from '../../utils/onlineStatus';

const syncEmergencyContactsFromParentFn = httpsCallable(functions, 'syncEmergencyContactsFromParent');
const syncMyAuthEmailFn = httpsCallable(functions, 'syncMyAuthEmail');

function getEmailChangeActionCodeSettings() {
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : '';

  return {
    url: `${origin}/profile`,
    handleCodeInApp: false,
  };
}

function normalizeEmail(value = '') {
  return String(value || '').trim().toLowerCase();
}

function formatEmailChangeError(error) {
  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/email-already-in-use':
      return 'That email address is already used by another account.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'That password did not match your current account.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a little while, then try again.';
    case 'auth/requires-recent-login':
      return 'Please log out and back in before changing your email address.';
    default:
      return 'Could not send the email change verification. Please try again.';
  }
}

export default function ProfileForm({ userId, onSave }) {
  const [email, setEmail] = useState('');
  const [emailChangeOpen, setEmailChangeOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmNewEmail, setConfirmNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [emailChangeSaving, setEmailChangeSaving] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  const [emailChangeMessage, setEmailChangeMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [origPhone, setOrigPhone] = useState('');
  const [address, setAddress] = useState('');
  const [origAddress, setOrigAddress] = useState('');
  const [username, setUsername] = useState('');
  const [origUsername, setOrigUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [origFirstName, setOrigFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [origLastName, setOrigLastName] = useState('');
  const [dob, setDob] = useState('');
  const [origDob, setOrigDob] = useState('');
  const [available, setAvailable] = useState(null);
  const [autoSyncFamilyEmergencyContacts, setAutoSyncFamilyEmergencyContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const online = useOnlineStatus();

  useEffect(() => {
    (async () => {
      if (!userId) return;
      const currentAuthEmail = auth.currentUser?.email || '';
      setEmail(currentAuthEmail);

      try {
        const [userSnap, privateSnap] = await Promise.all([
          getDoc(doc(db, 'users', userId)),
          getDoc(doc(db, 'privateUsers', userId)),
        ]);
        if (userSnap?.exists?.()) {
          const data = userSnap.data();
          const privateData = privateSnap?.exists?.() ? privateSnap.data() || {} : {};
          const uname = data.username || '';
          const fName = data.firstName || '';
          const lName = data.lastName || '';
          const birth = privateData.dob || '';
          const mobile = privateData.phone || '';
          const homeAddress = privateData.address || '';

          setUsername(uname);
          setOrigUsername(uname);
          setFirstName(fName);
          setOrigFirstName(fName);
          setLastName(lName);
          setOrigLastName(lName);
          setPhone(mobile);
          setOrigPhone(mobile);
          setAddress(homeAddress);
          setOrigAddress(homeAddress);
          setDob(birth);
          setOrigDob(birth);
          setAutoSyncFamilyEmergencyContacts(Boolean(privateData.autoSyncFamilyEmergencyContacts));
        }
      } catch (err) {
        // Keep the account email visible even if profile details fail to load.
      }

      if (!online || !currentAuthEmail) return;
      syncMyAuthEmailFn()
        .then((result) => {
          const syncedEmail = result?.data?.email || '';
          if (syncedEmail) setEmail(syncedEmail);
        })
        .catch(() => {});
    })();
  }, [online, userId]);

  const isDirty =
    username.trim().toLowerCase() !== origUsername.trim().toLowerCase() ||
    firstName !== origFirstName ||
    lastName !== origLastName ||
    phone !== origPhone ||
    address !== origAddress ||
    dob !== origDob;

  const prepareProfileData = () => {
    const normalizedUsername = username.trim().toLowerCase();
    const normalizedOriginalUsername = origUsername.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhone = phone.trim();
    const trimmedAddress = address.trim();
    const trimmedDob = dob.trim();
    const usernameChanged = normalizedUsername !== normalizedOriginalUsername;

    return {
      normalizedUsername,
      trimmedFirstName,
      trimmedLastName,
      trimmedPhone,
      trimmedAddress,
      trimmedDob,
      usernameChanged,
    };
  };

  const validateProfileData = ({
    normalizedUsername,
    trimmedFirstName,
    trimmedLastName,
    trimmedPhone,
    trimmedAddress,
    trimmedDob,
    usernameChanged,
  }) => {
    if (!normalizedUsername) {
      return 'Please enter a username.';
    }
    if (!trimmedFirstName || !trimmedLastName || !trimmedDob) {
      return 'First name, last name, and date of birth are required.';
    }
    if (autoSyncFamilyEmergencyContacts && (!trimmedPhone || !trimmedAddress)) {
      return 'Turn off child account sync before removing your phone number or address.';
    }
    if (usernameChanged && !available) {
      return 'Please choose a different username.';
    }
    return null;
  };

  const clearProfileReminderNotifications = async () => {
    const existingQuery = query(
      collection(db, 'notifications', userId, 'items'),
      where('type', '==', 'reminder-profile-check')
    );
    const existingSnap = await getDocs(existingQuery);
    if (existingSnap.empty) return;

    await Promise.all(
      existingSnap.docs.map((snap) => deleteDoc(snap.ref).catch(() => {}))
    );
  };

  const syncFamilyContactsIfNeeded = async () => {
    if (!autoSyncFamilyEmergencyContacts) return '';

    try {
      const syncResult = await syncEmergencyContactsFromParentFn();
      if (
        syncResult?.data?.missingParentPhone === true
        || syncResult?.data?.missingParentAddress === true
        || syncResult?.data?.missingParentContacts === true
      ) {
        return 'Profile saved, but add both a phone number and address before syncing yourself as the emergency contact for child accounts.';
      }
    } catch (syncErr) {
      return syncErr?.message || 'Profile saved, but syncing your family emergency contact failed.';
    }

    return '';
  };

  const resetEmailChangeForm = () => {
    setNewEmail('');
    setConfirmNewEmail('');
    setCurrentPassword('');
    setEmailChangeError('');
    setEmailChangeMessage('');
  };

  const handleEmailChangeRequest = async () => {
    setEmailChangeError('');
    setEmailChangeMessage('');

    if (!online) {
      setEmailChangeError(offlineWriteMessage('change your email address'));
      return;
    }

    const currentUser = auth.currentUser;
    const currentEmail = normalizeEmail(currentUser?.email || email);
    const nextEmail = normalizeEmail(newEmail);
    const nextEmailConfirmation = normalizeEmail(confirmNewEmail);

    if (!currentUser || !currentEmail) {
      setEmailChangeError('Please log in again before changing your email address.');
      return;
    }
    if (!nextEmail) {
      setEmailChangeError('Enter a new email address.');
      return;
    }
    if (nextEmail === currentEmail) {
      setEmailChangeError('Enter a different email address.');
      return;
    }
    if (nextEmail !== nextEmailConfirmation) {
      setEmailChangeError('Email addresses do not match.');
      return;
    }
    if (!currentPassword) {
      setEmailChangeError('Enter your current password.');
      return;
    }

    try {
      setEmailChangeSaving(true);
      const credential = EmailAuthProvider.credential(currentEmail, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await verifyBeforeUpdateEmail(currentUser, nextEmail, getEmailChangeActionCodeSettings());
      setEmailChangeMessage('Check your new email address to confirm the change.');
      setNewEmail('');
      setConfirmNewEmail('');
      setCurrentPassword('');
    } catch (err) {
      setEmailChangeError(formatEmailChangeError(err));
    } finally {
      setEmailChangeSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage('');
    if (!online) {
      setError(offlineWriteMessage('save profile changes'));
      return;
    }

    const profileData = prepareProfileData();
    const validationError = validateProfileData(profileData);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      await setDoc(
        doc(db, 'users', userId),
        {
          username: profileData.normalizedUsername,
          firstName: profileData.trimmedFirstName,
          lastName: profileData.trimmedLastName,
          dob: deleteField(),
        },
        { merge: true }
      );
      await setDoc(
        doc(db, 'privateUsers', userId),
        {
          dob: profileData.trimmedDob,
          phone: profileData.trimmedPhone || deleteField(),
          address: profileData.trimmedAddress || deleteField(),
          profileLastModified: serverTimestamp(),
        },
        { merge: true }
      );
      await clearProfileReminderNotifications();

      const syncMessage = await syncFamilyContactsIfNeeded();
      if (syncMessage) {
        setError(syncMessage);
      }

      setUsername(profileData.normalizedUsername);
      setOrigUsername(profileData.normalizedUsername);
      setFirstName(profileData.trimmedFirstName);
      setLastName(profileData.trimmedLastName);
      setPhone(profileData.trimmedPhone);
      setAddress(profileData.trimmedAddress);
      setDob(profileData.trimmedDob);
      setOrigFirstName(profileData.trimmedFirstName);
      setOrigLastName(profileData.trimmedLastName);
      setOrigPhone(profileData.trimmedPhone);
      setOrigAddress(profileData.trimmedAddress);
      setOrigDob(profileData.trimmedDob);
      setSuccessMessage('Profile saved successfully.');
      onSave?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmReview = async () => {
    setError(null);
    setSuccessMessage('');
    if (!online) {
      setError(offlineWriteMessage('confirm your profile review'));
      return;
    }

    const profileData = prepareProfileData();
    const validationError = validateProfileData(profileData);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      await setDoc(
        doc(db, 'privateUsers', userId),
        {
          profileLastModified: serverTimestamp(),
        },
        { merge: true }
      );
      await clearProfileReminderNotifications();
      setSuccessMessage('Profile review recorded.');
      onSave?.();
    } catch (err) {
      setError(err?.message || 'Could not confirm your profile review.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex-column" style={{ gap: '1rem' }}>
      <div className="flex-column" style={{ gap: '0.5rem' }}>
        <label htmlFor="profile-email" style={{ fontWeight: 600 }}>Email</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '0.5rem', alignItems: 'center' }}>
          <input
            id="profile-email"
            type="email"
            value={email}
            disabled
            className="form-input"
          />
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => {
              const opening = !emailChangeOpen;
              setEmailChangeOpen(opening);
              resetEmailChangeForm();
            }}
          >
            {emailChangeOpen ? 'Cancel' : 'Change email'}
          </button>
        </div>

        {emailChangeOpen && (
          <fieldset style={{ border: '1px solid var(--clr-border, #ddd)', borderRadius: '12px', padding: '1rem' }}>
            <legend style={{ fontWeight: 700 }}>Change account email</legend>
            <div className="flex-column" style={{ gap: '0.75rem' }}>
              <TextInput
                label="New email"
                inputId="profile-new-email"
                name="newEmail"
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
                autoComplete="email"
              />
              <TextInput
                label="Confirm new email"
                inputId="profile-confirm-new-email"
                name="confirmNewEmail"
                type="email"
                value={confirmNewEmail}
                onChange={(event) => setConfirmNewEmail(event.target.value)}
                autoComplete="email"
              />
              <TextInput
                label="Current password"
                inputId="profile-current-password"
                name="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="button"
                onClick={handleEmailChangeRequest}
                disabled={emailChangeSaving}
              >
                {emailChangeSaving ? 'Sending...' : 'Send verification email'}
              </button>
              <p className="text-muted" style={{ margin: 0 }}>
                Your login email changes only after you confirm the link sent to the new address.
              </p>
              {emailChangeMessage && <p style={{ color: 'green', margin: 0 }}>{emailChangeMessage}</p>}
              {emailChangeError && <ErrorMessage message={emailChangeError} />}
            </div>
          </fieldset>
        )}
      </div>
      <TextInput
        label="Phone Number"
        value={phone}
        onChange={e => setPhone(e.target.value)}
        type="tel"
        autoComplete="tel"
        required={autoSyncFamilyEmergencyContacts}
      />
      <TextInput
        label="Home Address"
        value={address}
        onChange={e => setAddress(e.target.value)}
        autoComplete="street-address"
        required={autoSyncFamilyEmergencyContacts}
      />
      {autoSyncFamilyEmergencyContacts && (
        <p className="text-muted" style={{ margin: 0 }}>
          Phone number and address are required while child account emergency sync is enabled.
        </p>
      )}
      <div className="flex-column" style={{ gap: '0.35rem' }}>
        <label htmlFor="profile-username" style={{ fontWeight: 600 }}>Username</label>
        <UsernameInput
          inputId="profile-username"
          value={username}
          onChange={setUsername}
          setAvailable={setAvailable}
          userId={userId}
          initialValue={origUsername}
        />
      </div>
      <TextInput
        label="First Name"
        value={firstName}
        onChange={e => setFirstName(e.target.value)}
        required
      />
      <TextInput
        label="Last Name"
        value={lastName}
        onChange={e => setLastName(e.target.value)}
        required
      />
      <DateInput
        label="Date of Birth"
        value={dob}
        onChange={e => setDob(e.target.value)}
        required
      />

      {isDirty ? (
        <SubmitButton
          label={saving ? 'Saving…' : 'Save Changes'}
          disabled={saving || !isDirty || !online}
        />
      ) : (
        <>
          <p className="text-muted" style={{ margin: 0 }}>
            No changes needed? Confirm that these details are still correct.
          </p>
          <SubmitButton
            type="button"
            label={saving ? 'Confirming…' : 'Details Still Correct'}
            onClick={handleConfirmReview}
            disabled={saving || !online}
          />
        </>
      )}

      {!online && <p className="text-muted" style={{ margin: 0 }}>Reconnect to update your profile.</p>}
      {successMessage && <p style={{ color: 'green', margin: 0 }}>{successMessage}</p>}
      {error && <ErrorMessage message={error} />}
    </form>
  );
}
